import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Check } from 'lucide-react';
type Product = {
  title: string;
  editionLabel?: string;
  priceNumber?: number;
  priceText?: string;
  thumbnailUrl?: string;
};
const FIRST_NAMES = ['Emma', 'Olivia', 'Sophia', 'Ava', 'Isabella', 'Mia', 'Charlotte', 'Amelia', 'Harper', 'Evelyn', 'Abigail', 'Ella', 'Scarlett', 'Grace', 'Chloe', 'Lily', 'Hannah', 'Layla', 'Nora', 'Zoe', 'Hazel', 'Riley', 'Aria', 'Stella', 'James', 'William', 'Benjamin', 'Lucas', 'Henry', 'Alexander', 'Michael', 'Daniel', 'Matthew', 'Jackson', 'David', 'Joseph', 'Samuel', 'Andrew', 'Logan', 'John', 'Owen', 'Wyatt', 'Dylan', 'Luke', 'Gabriel', 'Anthony', 'Patricia', 'Linda', 'Barbara', 'Susan', 'Jessica', 'Karen', 'Nancy', 'Margaret', 'Sandra', 'Dorothy', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Robert', 'Richard', 'Thomas', 'Charles', 'Christopher', 'Mark', 'Steven', 'Paul', 'Kevin', 'Brian', 'George', 'Edward', 'Ronald', 'Timothy', 'Jason'];
const LAST_INITIALS = ['A.', 'B.', 'C.', 'D.', 'F.', 'G.', 'H.', 'J.', 'K.', 'L.', 'M.', 'N.', 'P.', 'R.', 'S.', 'T.', 'W.'];
const US_CITIES = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'Indianapolis, IN', 'San Francisco, CA', 'Seattle, WA', 'Denver, CO', 'Washington, DC', 'Boston, MA', 'Nashville, TN', 'Portland, OR', 'Las Vegas, NV', 'Detroit, MI', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Sacramento, CA', 'Kansas City, MO', 'Atlanta, GA', 'Miami, FL', 'Raleigh, NC', 'Omaha, NE', 'Minneapolis, MN', 'Tulsa, OK', 'Cleveland, OH', 'Wichita, KS', 'Arlington, TX', 'New Orleans, LA', 'Bakersfield, CA', 'Tampa, FL', 'Honolulu, HI', 'Anaheim, CA', 'Santa Ana, CA', 'Corpus Christi, TX', 'Riverside, CA', 'St. Louis, MO', 'Lexington, KY', 'Pittsburgh, PA', 'Anchorage, AK', 'Stockton, CA', 'Cincinnati, OH', 'Saint Paul, MN', 'Toledo, OH', 'Greensboro, NC', 'Newark, NJ', 'Plano, TX', 'Henderson, NV', 'Lincoln, NE', 'Buffalo, NY', 'Jersey City, NJ', 'Chula Vista, CA', 'Orlando, FL', 'Norfolk, VA', 'Chandler, AZ', 'Laredo, TX', 'Madison, WI', 'Durham, NC', 'Lubbock, TX', 'Winston-Salem, NC', 'Garland, TX', 'Glendale, AZ', 'Hialeah, FL', 'Reno, NV', 'Baton Rouge, LA', 'Irvine, CA', 'Chesapeake, VA', 'Irving, TX', 'Scottsdale, AZ', 'North Las Vegas, NV', 'Fremont, CA', 'Boise, ID', 'Richmond, VA', 'San Bernardino, CA', 'Birmingham, AL', 'Spokane, WA', 'Rochester, NY', 'Des Moines, IA', 'Modesto, CA', 'Fayetteville, NC'];
const RANDOM_PRICES = [9.87, 17.86, 25.85];
function findThumbnail(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const imgs = Array.from(document.querySelectorAll('img')) as HTMLImageElement[];
  const coverImg = imgs.find(img => /cover|book|spine|11pm/i.test(img.alt || img.src));
  return coverImg?.src || imgs[0]?.src;
}
type Notification = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
export function RecentPurchaseToast() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const thumbnailRef = useRef<string | undefined>(undefined);
  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    const buildTimer = window.setTimeout(() => {
      thumbnailRef.current = findThumbnail();
    }, 500);
    let showTimer: number;
    let hideTimer: number;
    let active = true;
    const pickName = (): string => {
      const recent = recentNamesRef.current;
      let tries = 0;
      let candidate = '';
      do {
        const first = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
        const initial = LAST_INITIALS[Math.floor(Math.random() * LAST_INITIALS.length)];
        candidate = `${first} ${initial}`;
        tries++;
      } while (recent.includes(candidate) && tries < 20);
      recentNamesRef.current = [candidate, ...recent].slice(0, 10);
      return candidate;
    };
    const showOne = () => {
      if (!active) return;
      const randomPrice = RANDOM_PRICES[Math.floor(Math.random() * RANDOM_PRICES.length)];
      const next: Notification = {
        id: ++idRef.current,
        name: pickName(),
        city: US_CITIES[Math.floor(Math.random() * US_CITIES.length)],
        product: {
          title: 'The 11PM Spine Reset',
          editionLabel: 'The 11PM Spine Reset',
          priceNumber: randomPrice,
          priceText: '$' + randomPrice,
          thumbnailUrl: thumbnailRef.current
        }
      };
      setCurrent(next);
      hideTimer = window.setTimeout(() => {
        setCurrent(null);
        const nextDelay = 8000 + Math.random() * 7000;
        showTimer = window.setTimeout(showOne, nextDelay);
      }, 4000);
    };
    showTimer = window.setTimeout(showOne, 3000);
    return () => {
      active = false;
      window.clearTimeout(buildTimer);
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);
  const reduced = prefersReducedMotion.current;
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] pointer-events-none w-[calc(100%-2rem)] max-w-sm" data-id="element-259">
      <AnimatePresence data-id="element-260">
        {current && <motion.div key={current.id} initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: reduced ? 0.15 : 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-navy-800/95 backdrop-blur-md border border-navy-700 rounded-2xl shadow-2xl p-3 flex items-center gap-3" data-id="element-261">
            <div className="relative shrink-0" data-id="element-262">
              {current.product.thumbnailUrl ? <img src={current.product.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-navy-700" data-id="element-263" /> : <div className="w-12 h-12 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center" data-id="element-264">
                  <ShoppingBag className="w-5 h-5 text-teal-400" data-id="element-265" />
                </div>}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center border-2 border-navy-800" data-id="element-266">
                <Check className="w-3 h-3" strokeWidth={3} data-id="element-267" />
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-268">
              <p className="text-xs text-white/60 leading-tight" data-id="element-269">
                <span className="font-semibold text-white" data-id="element-270">{current.name}</span>{' '}
                in <span className="text-white/90" data-id="element-271">{current.city}</span>
              </p>
              <p className="text-sm text-white font-medium leading-snug mt-0.5 truncate" data-id="element-272">
                just purchased{' '}
                <span className="text-teal-400" data-id="element-273">
                  {current.product.editionLabel || current.product.title}
                </span>
              </p>
              <p className="text-[11px] text-white/50 mt-0.5" data-id="element-274">
                {current.product.priceText} · a few seconds ago
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}